import { FechaCR, MontoCRC, MontoUSD } from '@/components/dominio';
import { BankAccountPickerBC } from '@/components/filtros/BankAccountPickerBC';
import { estadoDeLista } from '@/components/solicitudes-contado/ListasContadoTabla';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import {
  useAccionesListaContado,
  useListaContado,
  useVistaPagoContado,
} from '@/hooks/useListasContado';
import { useUsuarioActual } from '@/hooks/useSolicitudesContado';
import { cliente } from '@/lib/cliente';
import { COLOR_BANCO } from '@/lib/coloresBanco';
import { descargarArchivoTexto } from '@/lib/descargar-archivo';
import { diasEntre, formatearFechaHoraCR } from '@/lib/fechaHora';
import { cn } from '@/lib/utils';
import type {
  ArchivoContado,
  ArchivoPagoContado,
  MotivoExclusionContado,
  SolicitudContado,
} from '@adelante/shared';
import {
  ArrowLeft,
  CircleNotch,
  DownloadSimple,
  LockSimple,
  LockSimpleOpen,
  PencilSimple,
  Trash,
  X,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const esDolares = (s: { currencyCode: string | null }) =>
  !!s.currencyCode && s.currencyCode !== 'CRC';

const TEXTO_EXCLUSION: Record<MotivoExclusionContado, string> = {
  'sin-proveedor': 'No es un proveedor de Proveedores (se paga a mano)',
  'sin-cuenta': 'El proveedor no tiene cuenta en esa moneda',
  'iban-formato': 'IBAN con formato inválido',
  'sin-cedula': 'Sin cédula (el BN la exige)',
  'cedula-incompleta': 'Cédula de 9 dígitos con formato de jurídica: falta un dígito',
};

const ESTADO_SOLICITUD: Record<
  SolicitudContado['estado'],
  { texto: string; variante: 'warning' | 'success' | 'destructive' | 'secondary' | 'outline' }
> = {
  SOLICITADA: { texto: 'Pendiente', variante: 'warning' },
  APROBADA: { texto: 'Por pagar', variante: 'success' },
  RECHAZADA: { texto: 'Rechazada', variante: 'destructive' },
  EN_PROPUESTA: { texto: 'En archivo', variante: 'secondary' },
  PAGADA: { texto: 'Pagada', variante: 'outline' },
};

/** Baja los archivos de un envío. BAC y BN van en Latin-1 (un byte por carácter); el CSV en UTF-8. */
function descargarArchivos(archivos: ArchivoContado[]) {
  for (const a of archivos) descargarArchivoTexto(a.contenido, a.nombre, !/\.csv$/i.test(a.nombre));
}

function Monto({ valor, dolares }: { valor: number; dolares: boolean }) {
  return dolares ? <MontoUSD monto={valor} /> : <MontoCRC monto={valor} />;
}

/** Un archivo ya generado (o un pago a mano) de la lista, con lo que se puede hacer con él. */
function TarjetaArchivo({ a, puedeDeshacer }: { a: ArchivoPagoContado; puedeDeshacer: boolean }) {
  const acciones = useAccionesListaContado();
  const dolares = a.moneda === 'USD';
  // Día del pago: se edita y se guarda con el botón (o con Enter), no al salir del campo.
  const [fechaPago, setFechaPago] = useState(a.fecha);

  function guardarFecha() {
    if (!fechaPago || fechaPago === a.fecha) return;
    acciones.cambiarFechaPago.mutate(
      { idArchivo: a.idArchivo, fecha: fechaPago },
      {
        onSuccess: () => toast.success('Fecha de pago actualizada'),
        onError: (er) => {
          setFechaPago(a.fecha);
          toast.error(er instanceof Error ? er.message : 'No se pudo cambiar');
        },
      },
    );
  }
  const titulo =
    a.tipo === 'MANUAL'
      ? 'Pago a mano'
      : a.tipo === 'BAC'
        ? 'Archivo BAC'
        : 'Archivo Banco Nacional';

  async function bajar() {
    try {
      const r = await cliente.listasContado.contenido(a.idArchivo);
      if (r.data) descargarArchivos(r.data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo bajar el archivo');
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-6 text-sm">
        <div className="min-w-[12rem]">
          <div className="flex items-center gap-2 font-medium">
            {a.tipo !== 'MANUAL' && (
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-xs',
                  COLOR_BANCO[a.tipo === 'BAC' ? 'BAC' : 'BN'].activo,
                )}
              >
                {a.tipo}
              </span>
            )}
            {titulo}
            <Badge variant={a.estado === 'PAGADO' ? 'success' : 'warning'}>
              {a.estado === 'PAGADO' ? 'Pagado' : 'Por confirmar'}
            </Badge>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {a.tipo === 'MANUAL'
              ? (a.referencia ?? 'Sin detalle')
              : a.tipo === 'BAC'
                ? `Cuenta ${a.cuentaBancoOrigen} · plan ${a.planCode} · envío ${a.envioNo}`
                : `Cuenta ${a.cuentaBancoOrigen} · transferencia ${a.envioNo}`}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">{a.cantidadPagos} pago(s)</div>
          <Monto valor={a.montoTotal} dolares={dolares} />
        </div>
        <div className="text-xs text-muted-foreground">
          {a.estado === 'PAGADO' ? (
            <div className="flex items-center gap-1.5">
              <label htmlFor={`fecha-pago-${a.idArchivo}`}>Fecha de pago</label>
              <Input
                id={`fecha-pago-${a.idArchivo}`}
                type="date"
                className="h-8 w-40 text-xs text-foreground"
                value={fechaPago}
                max={new Date().toLocaleDateString('en-CA')}
                title="Día en que se hizo el pago: decide en qué semana cuenta"
                onChange={(ev) => setFechaPago(ev.target.value)}
                onKeyDown={(ev) => ev.key === 'Enter' && guardarFecha()}
              />
              {fechaPago && fechaPago !== a.fecha && (
                <Button
                  size="sm"
                  className="h-8"
                  disabled={acciones.cambiarFechaPago.isPending}
                  onClick={guardarFecha}
                >
                  Guardar
                </Button>
              )}
            </div>
          ) : (
            <FechaCR fecha={a.fecha} />
          )}
          <div>{a.generadoPorEmail}</div>
          {a.pagadoEn && <div>Pagado por {a.pagadoPorEmail}</div>}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {a.nombres.length > 0 && (
            <Button size="sm" variant="outline" onClick={bajar}>
              <DownloadSimple className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              Bajar de nuevo
            </Button>
          )}
          {a.estado === 'GENERADO' && (
            <>
              <Button
                size="sm"
                disabled={acciones.marcarPagado.isPending}
                onClick={() =>
                  acciones.marcarPagado.mutate(a.idArchivo, {
                    onSuccess: () => toast.success('Archivo marcado como pagado'),
                    onError: (e) =>
                      toast.error(e instanceof Error ? e.message : 'No se pudo marcar'),
                  })
                }
              >
                Marcar pagado
              </Button>
              {puedeDeshacer && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground"
                  title="Si el archivo salió mal: las solicitudes vuelven a 'por pagar'"
                  disabled={acciones.deshacer.isPending}
                  onClick={() => {
                    if (!confirm('¿Deshacer este archivo? Las solicitudes vuelven a "por pagar".'))
                      return;
                    acciones.deshacer.mutate(a.idArchivo, {
                      onSuccess: () => toast.success('Archivo deshecho'),
                      onError: (e) =>
                        toast.error(e instanceof Error ? e.message : 'No se pudo deshacer'),
                    });
                  }}
                >
                  Deshacer
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Un archivo de banco: lo que sale de una cuenta de origen con las solicitudes que le tocan. */
function PagoPorCuenta({
  idLista,
  cuenta,
  ids,
  nombreCuenta,
}: {
  idLista: number;
  cuenta: string;
  ids: number[];
  nombreCuenta: string;
}) {
  const acciones = useAccionesListaContado();
  const [planCode, setPlanCode] = useState('');
  const [envioNo, setEnvioNo] = useState('');
  const [transferenciaNo, setTransferenciaNo] = useState('');
  // Cuenta del proveedor elegida a mano para este pago (código de proveedor → id de cuenta).
  const [cuentasElegidas, setCuentasElegidas] = useState<Record<string, number>>({});
  const {
    data: vistaData,
    isFetching: cargandoVista,
    error: errorVista,
  } = useVistaPagoContado(idLista, cuenta, ids, true, cuentasElegidas);
  const vista = vistaData?.data;

  const planDefecto = vista?.ultimo?.planCode ?? '';
  const envioDefecto = vista?.ultimo ? String(vista.ultimo.envioNo + 1) : '';
  const planFinal = planCode || planDefecto;
  const envioFinal = envioNo || envioDefecto;

  const faltaDato: string | null = (() => {
    if (!vista) return 'Calculando…';
    if (vista.bloqueos.length > 0) return vista.bloqueos[0] ?? null;
    if (vista.banco === 'BAC' && (!planFinal || !(Number(envioFinal) > 0)))
      return 'Indicá el plan (BOK) y el número de envío';
    if (vista.banco === 'BN' && !(Number(transferenciaNo) > 0))
      return 'Indicá el número de transferencia del BN';
    return null;
  })();

  function generar() {
    if (faltaDato || !vista) return;
    acciones.generar.mutate(
      {
        id: idLista,
        datos: {
          cuentaBancoOrigen: cuenta,
          solicitudIds: ids,
          cuentasElegidas,
          ...(vista.banco === 'BAC'
            ? { planCode: planFinal, envioNo: Number(envioFinal) }
            : { transferenciaNo: Number(transferenciaNo) }),
        },
      },
      {
        onSuccess: (r) => {
          if (r.data) {
            descargarArchivos(r.data.archivos);
            toast.success(
              `Archivo generado: ${r.data.archivo.cantidadPagos} pago(s). Quedó guardado en la lista.`,
            );
            if (r.data.excluidas.length > 0)
              toast.warning(
                `${r.data.excluidas.length} solicitud(es) no entraron: revisá los motivos`,
              );
          }
          setTransferenciaNo('');
          setEnvioNo('');
        },
        onError: (er) => toast.error(er instanceof Error ? er.message : 'No se pudo generar'),
      },
    );
  }

  return (
    <div className="space-y-4 rounded-md border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-medium">{nombreCuenta}</span>
        {vista?.banco && (
          <span
            className={cn('rounded px-2 py-1 text-xs font-medium', COLOR_BANCO[vista.banco].activo)}
          >
            {vista.banco === 'BAC' ? 'BAC' : 'Banco Nacional'} ·{' '}
            {vista.moneda === 'D' ? 'dólares' : 'colones'}
          </span>
        )}
        <span className="text-xs text-muted-foreground">{ids.length} solicitud(es)</span>
        {cargandoVista && (
          <CircleNotch
            className="h-4 w-4 animate-spin text-muted-foreground"
            weight="bold"
            aria-label="Calculando"
          />
        )}
      </div>
      {errorVista && (
        <p className="text-sm text-red-600">
          {errorVista instanceof Error ? errorVista.message : 'No se pudo calcular'}
        </p>
      )}

      {vista && (
        <>
          {vista.bloqueos.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {vista.bloqueos.map((b) => (
                <div key={b}>{b}</div>
              ))}
            </div>
          )}
          {vista.pagos.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Cuenta destino</TableHead>
                  <TableHead>Cómo viaja</TableHead>
                  <TableHead className="text-right">A pagar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vista.pagos.map((p) => (
                  <TableRow key={`${p.vendorCode}${p.iban}`}>
                    <TableCell className="font-medium">
                      {p.nombre}
                      <div className="text-xs font-normal text-muted-foreground">
                        {p.solicitudes.length} solicitud(es)
                      </div>
                    </TableCell>
                    <TableCell>
                      {p.cuentasDisponibles.length > 1 ? (
                        <select
                          aria-label={`Cuenta destino de ${p.nombre}`}
                          value={p.cuentaId}
                          onChange={(ev) =>
                            setCuentasElegidas((c) => ({
                              ...c,
                              [p.vendorCode]: Number(ev.target.value),
                            }))
                          }
                          className={cn(
                            'rounded border border-input px-1.5 py-0.5 font-mono text-xs',
                            COLOR_BANCO[p.bancoCuenta].suave,
                          )}
                        >
                          {p.cuentasDisponibles.map((c) => (
                            <option key={c.id} value={c.id}>
                              …{c.iban.slice(-4)} ·{' '}
                              {c.bancoCuenta === 'OTRO' ? 'otro banco' : c.bancoCuenta}
                              {c.predeterminada ? ' (predeterminada)' : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={cn(
                            'rounded px-1.5 py-0.5 font-mono text-xs',
                            COLOR_BANCO[p.bancoCuenta].suave,
                          )}
                        >
                          …{p.iban.slice(-4)} ·{' '}
                          {p.bancoCuenta === 'OTRO' ? 'otro banco' : p.bancoCuenta}
                        </span>
                      )}
                      {p.advertencia && (
                        <div className="text-xs text-amber-700">{p.advertencia}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {p.tipo === 'TRF' ? (
                        <span className="text-muted-foreground">TRF · sin comisión</span>
                      ) : p.tipo === 'SINPE' ? (
                        <span className="font-medium text-amber-700">SINPE · con comisión</span>
                      ) : (
                        <span className="text-muted-foreground">BAC</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Monto valor={p.total} dolares={vista.moneda === 'D'} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} className="text-right font-medium">
                    Total del archivo ({vista.pagos.length} pago(s))
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    <Monto valor={vista.total} dolares={vista.moneda === 'D'} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          {vista.excluidos.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
              <div className="mb-1 font-medium text-amber-900">
                No entran en este archivo ({vista.excluidos.length}):
              </div>
              {vista.excluidos.map((x) => (
                <div key={x.idSolicitud} className="text-amber-900">
                  {x.nombre} — {x.descripcion}:{' '}
                  <span className="text-amber-800">{TEXTO_EXCLUSION[x.motivo]}</span>
                  {x.vendorCode && (
                    <Link
                      to={`/flujo-semanal/proveedores?vendorCode=${encodeURIComponent(x.vendorCode)}&nombre=${encodeURIComponent(x.nombre)}&moneda=${x.moneda}`}
                      className="ml-2 font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950"
                    >
                      {x.motivo === 'sin-proveedor'
                        ? 'Crear proveedor →'
                        : x.motivo === 'sin-cuenta'
                          ? 'Agregar cuenta →'
                          : 'Corregir datos →'}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
          {vista.otraMoneda.cantidad > 0 && (
            <p className="text-xs text-muted-foreground">
              {vista.otraMoneda.cantidad} solicitud(es) marcada(s) son de la otra moneda (
              {vista.otraMoneda.nombres.join(', ')}) y no entran en este archivo. Si van por una
              cuenta de esa moneda, generá el archivo con esa cuenta; si es un proveedor
              internacional (transferencia al exterior, sin archivo de banco), pagalas con "Pago a
              mano": desmarcá las demás y dejá marcadas solo esas.
            </p>
          )}

          {vista.pagos.length > 0 && vista.bloqueos.length === 0 && (
            <div className="flex flex-wrap items-end gap-4">
              {vista.banco === 'BAC' ? (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="plan-bac">Plan (BOK)</Label>
                    <Input
                      id="plan-bac"
                      value={planFinal}
                      onChange={(ev) => setPlanCode(ev.target.value)}
                      className="w-32"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="envio-bac">Envío n.º</Label>
                    <Input
                      id="envio-bac"
                      inputMode="numeric"
                      value={envioFinal}
                      onChange={(ev) => setEnvioNo(ev.target.value)}
                      className="w-24"
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-1">
                  <Label htmlFor="transferencia-bn">Transferencia n.º</Label>
                  <Input
                    id="transferencia-bn"
                    inputMode="numeric"
                    value={transferenciaNo}
                    onChange={(ev) => setTransferenciaNo(ev.target.value)}
                    className="w-32"
                  />
                </div>
              )}
              <Button disabled={!!faltaDato || acciones.generar.isPending} onClick={generar}>
                <DownloadSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                {acciones.generar.isPending ? 'Generando…' : 'Generar archivo'}
              </Button>
              {faltaDato && <span className="text-xs text-muted-foreground">{faltaDato}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ListaContadoDetallePantalla() {
  const { id } = useParams();
  const idLista = Number(id);
  const navigate = useNavigate();
  // Si se llegó desde Solicitudes de contado, volver allí; si no, a Pago Proveedores.
  const desdeSolicitudes =
    (useLocation().state as { desde?: string } | null)?.desde === 'solicitudes';
  const rutaVolver = desdeSolicitudes
    ? '/vencimientos/solicitudes'
    : '/vencimientos/propuestas?tipo=contado';
  const { data, isLoading } = useListaContado(Number.isFinite(idLista) ? idLista : null);
  const { data: yoData } = useUsuarioActual();
  const { data: cuentasData } = useCuentasBancoBC();
  const acciones = useAccionesListaContado();

  const [editandoNombre, setEditandoNombre] = useState(false);
  const [nombre, setNombre] = useState('');
  // Cuenta de pago por defecto y, por solicitud, la cuenta elegida aparte (si no, la de por defecto).
  const [cuenta, setCuenta] = useState<string | null>(null);
  const [cuentaPorSolicitud, setCuentaPorSolicitud] = useState<Record<number, string>>({});
  const [desmarcadas, setDesmarcadas] = useState<Set<number>>(new Set());
  const [referenciaMano, setReferenciaMano] = useState('');
  // Día en que se hizo el pago por fuera de la app (por defecto hoy): decide en qué semana cuenta.
  const [fechaPago, setFechaPago] = useState(() => new Date().toLocaleDateString('en-CA'));

  const lista = data?.data;
  const esAdmin = !!yoData?.data?.esAdmin;
  const puedePagar = !!yoData?.data?.puedePagar;
  const cerrada = lista?.estado === 'CERRADA';
  const cuentasBC = cuentasData?.data ?? [];
  const monedaDeCuenta = (numero: string) =>
    (cuentasBC.find((c) => c.number === numero)?.currencyCode || 'CRC') === 'CRC' ? 'CRC' : 'USD';
  const nombreDeCuenta = (numero: string) => {
    const c = cuentasBC.find((x) => x.number === numero);
    return c ? `${c.displayName} (${c.number})` : numero;
  };

  const porPagar = useMemo(
    () => (lista?.solicitudes ?? []).filter((s) => s.estado === 'APROBADA'),
    [lista],
  );
  const marcadas = porPagar.filter((s) => !desmarcadas.has(s.idSolicitud));
  const idsMarcadas = marcadas.map((s) => s.idSolicitud);

  /** Cuenta de la que sale esa solicitud: la elegida para ella o la de por defecto si es de su moneda. */
  function cuentaEfectiva(s: SolicitudContado): string | null {
    const propia = cuentaPorSolicitud[s.idSolicitud];
    if (propia) return propia;
    if (cuenta && monedaDeCuenta(cuenta) === (esDolares(s) ? 'USD' : 'CRC')) return cuenta;
    return null;
  }
  // Un archivo por cuenta de origen; las marcadas sin cuenta (otra moneda) quedan avisadas.
  const grupos = new Map<string, number[]>();
  const sinCuenta: SolicitudContado[] = [];
  for (const s of marcadas) {
    const c = cuentaEfectiva(s);
    if (!c) sinCuenta.push(s);
    else grupos.set(c, [...(grupos.get(c) ?? []), s.idSolicitud]);
  }

  if (isLoading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando…
      </div>
    );
  if (!lista)
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          No se encontró la lista.
        </CardContent>
      </Card>
    );

  const e = estadoDeLista(lista);
  const hayArchivos = lista.archivos.length > 0;

  function alternar(idSolicitud: number) {
    setDesmarcadas((actual) => {
      const nuevo = new Set(actual);
      if (nuevo.has(idSolicitud)) nuevo.delete(idSolicitud);
      else nuevo.add(idSolicitud);
      return nuevo;
    });
  }

  function guardarNombre() {
    const limpio = nombre.trim();
    if (!limpio || limpio === lista?.nombre) {
      setEditandoNombre(false);
      return;
    }
    acciones.renombrar.mutate(
      { id: idLista, nombre: limpio },
      {
        onSuccess: () => {
          toast.success('Lista renombrada');
          setEditandoNombre(false);
        },
        onError: (er) => toast.error(er instanceof Error ? er.message : 'No se pudo renombrar'),
      },
    );
  }

  function pagarAMano() {
    if (idsMarcadas.length === 0) return;
    acciones.pagarAMano.mutate(
      {
        id: idLista,
        datos: {
          solicitudIds: idsMarcadas,
          referencia: referenciaMano.trim() || null,
          fechaPago,
        },
      },
      {
        onSuccess: () => {
          toast.success('Pago a mano registrado');
          setReferenciaMano('');
        },
        onError: (er) => toast.error(er instanceof Error ? er.message : 'No se pudo registrar'),
      },
    );
  }

  function marcarListaPagada() {
    if (
      !confirm(
        `¿Marcar toda la lista como pagada? Las ${porPagar.length} solicitudes aprobadas pasan a "Pagada" (un registro por moneda). Úselo cuando ya se pagaron por fuera de la app.`,
      )
    )
      return;
    acciones.marcarPagada.mutate(
      { id: idLista, referencia: referenciaMano.trim() || null, fechaPago },
      {
        onSuccess: () => {
          toast.success('Lista marcada como pagada');
          setReferenciaMano('');
        },
        onError: (er) => toast.error(er instanceof Error ? er.message : 'No se pudo marcar'),
      },
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start gap-3">
        <Button
          variant="ghost"
          size="icon"
          title={desdeSolicitudes ? 'Volver a Solicitudes de contado' : 'Volver a Pago Proveedores'}
          onClick={() => navigate(rutaVolver)}
        >
          <ArrowLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {editandoNombre ? (
              <>
                <Input
                  value={nombre}
                  onChange={(ev) => setNombre(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') guardarNombre();
                    if (ev.key === 'Escape') setEditandoNombre(false);
                  }}
                  maxLength={100}
                  className="h-9 w-72"
                />
                <Button size="sm" onClick={guardarNombre}>
                  Guardar
                </Button>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-semibold tracking-tight">{lista.nombre}</h1>
                {puedePagar && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground"
                    title="Cambiar el nombre"
                    onClick={() => {
                      setNombre(lista.nombre);
                      setEditandoNombre(true);
                    }}
                  >
                    <PencilSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                  </Button>
                )}
              </>
            )}
            <Badge variant={e.variante}>{e.texto}</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Abierta el <FechaCR fecha={lista.fecha} /> por {lista.creadaPorEmail}
            {lista.cerradaEn && (
              <>
                {' · '}cerrada el <FechaCR fecha={lista.cerradaEn} /> por {lista.cerradaPorEmail}
              </>
            )}
          </p>
        </div>
        {puedePagar && (
          <div className="flex flex-wrap gap-2">
            {lista.estado === 'ABIERTA' && (
              <Button
                disabled={porPagar.length === 0 || acciones.cerrar.isPending}
                title="Termina la aprobación: Contabilidad la ve lista para pagar"
                onClick={() =>
                  acciones.cerrar.mutate(idLista, {
                    onSuccess: () => toast.success('Lista cerrada: lista para pagar'),
                    onError: (er) =>
                      toast.error(er instanceof Error ? er.message : 'No se pudo cerrar'),
                  })
                }
              >
                <LockSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                Cerrar y pasar a Contabilidad
              </Button>
            )}
            {esAdmin && cerrada && !hayArchivos && (
              <Button
                variant="outline"
                onClick={() =>
                  acciones.reabrir.mutate(idLista, {
                    onSuccess: () => toast.success('Lista reabierta'),
                    onError: (er) =>
                      toast.error(er instanceof Error ? er.message : 'No se pudo reabrir'),
                  })
                }
              >
                <LockSimpleOpen className="h-4 w-4" weight="bold" aria-hidden="true" />
                Reabrir lista
              </Button>
            )}
            {!hayArchivos && (
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => {
                  if (
                    !confirm(
                      `¿Eliminar "${lista.nombre}"? Sus ${lista.cantidadSolicitudes} solicitud(es) vuelven a pendientes.`,
                    )
                  )
                    return;
                  acciones.eliminar.mutate(idLista, {
                    onSuccess: () => {
                      toast.success('Lista eliminada');
                      navigate(rutaVolver);
                    },
                    onError: (er) =>
                      toast.error(er instanceof Error ? er.message : 'No se pudo eliminar'),
                  });
                }}
              >
                <Trash className="h-4 w-4" weight="bold" aria-hidden="true" />
                Eliminar
              </Button>
            )}
          </div>
        )}
      </div>

      {lista.estado === 'ABIERTA' && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-3 text-sm text-amber-900">
            La lista sigue abierta: todo lo que se apruebe en Solicitudes cae aquí.
            {esAdmin
              ? ' Cuando termine de aprobar, ciérrela para que Contabilidad pueda pagarla.'
              : ' Cuando el administrador la cierre se podrá pagar.'}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {lista.totales.map((t) => (
          <Card key={t.moneda}>
            <CardContent className="flex items-center justify-between gap-4 pt-6">
              <span className="text-sm font-medium">
                {t.moneda === 'USD' ? 'Dólares' : 'Colones'}
              </span>
              <div className="flex gap-6 text-sm">
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Solicitado</div>
                  <Monto valor={t.solicitado} dolares={t.moneda === 'USD'} />
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Aprobado</div>
                  <Monto valor={t.aprobado} dolares={t.moneda === 'USD'} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Solicitudes aprobadas ({lista.solicitudes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {cerrada && porPagar.length > 0 && <TableHead className="w-8" />}
                <TableHead>Proveedor</TableHead>
                <TableHead>Solicitada</TableHead>
                <TableHead>Documento</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="text-right">Solicitado</TableHead>
                <TableHead className="text-right">Aprobado</TableHead>
                <TableHead>Estado</TableHead>
                {puedePagar && cerrada && porPagar.length > 0 && <TableHead>Pagar desde</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lista.solicitudes.map((s) => {
                const b = ESTADO_SOLICITUD[s.estado];
                const dolares = esDolares(s);
                return (
                  <TableRow key={s.idSolicitud}>
                    {cerrada && porPagar.length > 0 && (
                      <TableCell>
                        {s.estado === 'APROBADA' && (
                          <input
                            type="checkbox"
                            aria-label={`Incluir ${s.nombre}`}
                            checked={!desmarcadas.has(s.idSolicitud)}
                            onChange={() => alternar(s.idSolicitud)}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="font-medium">{s.nombre}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      <FechaCR fecha={s.fecha} />
                      {s.resueltoEn && (
                        <div
                          className="text-muted-foreground"
                          title={`Aprobada por ${s.resueltoPorEmail}`}
                        >
                          aprobada {formatearFechaHoraCR(s.resueltoEn)} ·{' '}
                          {diasEntre(s.fecha, s.resueltoEn)} día(s)
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="min-w-[8rem] text-xs">{s.documentoBC ?? '—'}</TableCell>
                    <TableCell className="max-w-xs">{s.descripcion}</TableCell>
                    <TableCell className="text-right">
                      <Monto valor={s.montoSolicitado} dolares={dolares} />
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right',
                        s.montoAprobado !== null &&
                          s.montoAprobado < s.montoSolicitado &&
                          'font-medium text-amber-700',
                      )}
                    >
                      {s.montoAprobado !== null ? (
                        <Monto valor={s.montoAprobado} dolares={dolares} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={b.variante}>{b.texto}</Badge>
                    </TableCell>
                    {puedePagar && cerrada && porPagar.length > 0 && (
                      <TableCell>
                        {s.estado === 'APROBADA' && !desmarcadas.has(s.idSolicitud) && (
                          <div className="flex items-center gap-1">
                            <BankAccountPickerBC
                              value={cuentaEfectiva(s)}
                              onChange={(c) =>
                                setCuentaPorSolicitud((m) => {
                                  const nuevo = { ...m };
                                  if (c) nuevo[s.idSolicitud] = c;
                                  else delete nuevo[s.idSolicitud];
                                  return nuevo;
                                })
                              }
                              soloMoneda={dolares ? 'USD' : 'CRC'}
                              className="h-8 w-48 justify-between text-xs font-normal"
                            />
                            {cuentaPorSolicitud[s.idSolicitud] && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title="Volver a la cuenta por defecto"
                                onClick={() =>
                                  setCuentaPorSolicitud((m) => {
                                    const nuevo = { ...m };
                                    delete nuevo[s.idSolicitud];
                                    return nuevo;
                                  })
                                }
                              >
                                <X className="h-3 w-3" weight="bold" aria-hidden="true" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {puedePagar && cerrada && porPagar.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pagar</CardTitle>
            <p className="text-sm text-muted-foreground">
              Elija la cuenta de la que se paga: sale un archivo por cuenta, con una línea por
              proveedor (la suma de lo aprobado). Para pagar alguna solicitud desde otra cuenta,
              cámbiela en la columna "Pagar desde" de la tabla de arriba: se generan archivos
              separados. Las cuentas son de una sola moneda, así que lo de dólares necesita su
              propia cuenta en dólares.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label>Cuenta de pago por defecto</Label>
              <BankAccountPickerBC
                value={cuenta}
                onChange={setCuenta}
                className="w-[320px] justify-between font-normal"
              />
            </div>

            {sinCuenta.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Sin cuenta de pago ({sinCuenta.length}):{' '}
                {sinCuenta
                  .map((s) => `${s.nombre} (${esDolares(s) ? 'dólares' : 'colones'})`)
                  .join(', ')}
                . Elija una cuenta de su moneda en la columna "Pagar desde". Si es un proveedor
                internacional (transferencia al exterior, sin archivo de banco), use "Pago a mano".
              </div>
            )}

            {[...grupos.entries()].map(([c, ids]) => (
              <PagoPorCuenta
                key={c}
                idLista={idLista}
                cuenta={c}
                ids={ids}
                nombreCuenta={nombreDeCuenta(c)}
              />
            ))}

            <div className="flex flex-wrap items-end gap-3 border-t pt-4">
              <div className="space-y-1">
                <Label htmlFor="fecha-pago-mano">Fecha del pago</Label>
                <Input
                  id="fecha-pago-mano"
                  type="date"
                  value={fechaPago}
                  max={new Date().toLocaleDateString('en-CA')}
                  onChange={(ev) => setFechaPago(ev.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ref-mano">Pago a mano (sin archivo)</Label>
                <Input
                  id="ref-mano"
                  value={referenciaMano}
                  onChange={(ev) => setReferenciaMano(ev.target.value)}
                  placeholder="Cómo se pagó (SINPE móvil, transferencias 1 a 1…)"
                  maxLength={200}
                  className="w-96"
                />
              </div>
              <Button
                variant="outline"
                disabled={idsMarcadas.length === 0 || !fechaPago || acciones.pagarAMano.isPending}
                onClick={pagarAMano}
              >
                Registrar pago a mano de las {idsMarcadas.length} marcadas
              </Button>
              <Button
                variant="outline"
                title="Para una lista que ya se pagó por fuera de la app: registra todo lo aprobado, en colones y en dólares"
                disabled={porPagar.length === 0 || !fechaPago || acciones.marcarPagada.isPending}
                onClick={marcarListaPagada}
              >
                Marcar toda la lista como pagada
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {lista.archivos.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Archivos de esta lista</h2>
          {lista.archivos.map((a) => (
            <TarjetaArchivo key={a.idArchivo} a={a} puedeDeshacer />
          ))}
        </div>
      )}
    </div>
  );
}
